import { useToast } from '@chakra-ui/react';
import { getTenantByHost } from '@pelicargo/tenants';
import { NotificationCategory } from '@pelicargo/types';
import { modelUtils } from '@pelicargo/utils';
import { uniqBy } from 'lodash';
import { useMemo, useState } from 'react';

import { trpc } from '../../config/trpc';
import { useCustomerQuoteUpdateForm } from '../../hooks/emails/useCustomerQuoteUpdateForm';
import { useCurrentRequest } from '../../hooks/useCurrentRequest';
import { useEmail } from '../../hooks/useEmail';
import { hasCustomerEmail } from '../../utils/email';
import { EmailDrawer } from '../Emails/EmailDrawer';
import { EmailDrawerLeftPanel } from '../Emails/EmailDrawerLeftPanel';
import { EmailDrawerRightPanel } from '../Emails/EmailDrawerRightPanel';
import { EmailQuoteForm } from '../Emails/EmailQuoteForm';
import { useRequestDrawersContext } from '../RequestDrawers';
import { SimpleCustomerRecipientForm } from '../SimpleCustomerRecipientForm';
import { SimpleForm } from '../SimpleForm';

export const BookingConfirmDrawer = () => {
  const toast = useToast();
  const { request, quotes } = useCurrentRequest();
  const { methods } = useCustomerQuoteUpdateForm(request);
  const recipients = methods.watch('recipients');
  const [quoteIds] = useState<number[]>([]);
  const { data: contacts } = trpc.getUsersContacts.useQuery(
    { ids: [request?.default_sender_contact?.user?.id] },
    { enabled: !!request?.default_sender_contact?.user?.id },
  );
  const { data: defaultContacts } = trpc.getDefaultContactsByRequest.useQuery(
    { id: request?.id, updates_for: NotificationCategory.Booking },
    { enabled: !!request?.id },
  );
  const defaultSelectedContacts = uniqBy([...(contacts || []), ...(defaultContacts || [])].filter(Boolean), 'id');

  const { sendBookingConfirmNotification, isLoading } = useEmail();

  const { getIsModalOpen, closeModal } = useRequestDrawersContext();
  const isOpen = getIsModalOpen('BookingConfirmation');
  const onClose = () => closeModal('BookingConfirmation');

  const defaultRequest = useMemo(() => {
    const bookedQuote = request?.booked_quote_variant?.quote;
    const origin = bookedQuote?.origin_airport_id ?? request?.origin_airports?.[0]?.id;
    const destination =
      bookedQuote?.destination_airport_id ??
      request?.destination_airports?.[request?.destination_airports?.length - 1]?.id;
    const customMessage = `Your booking for request R${request?.id} from ${origin} to ${destination} is confirmed.`;

    return {
      ...request,
      customMessage,
    };
  }, [request]);

  const handleSendEmail = async (overrides: any) => {
    try {
      const requestId = request?.id;
      if (!requestId) return;

      if (quoteIds.length > 1) throw new Error('Please select one quote to book');

      const { senderContactId } = overrides;
      const toContact = recipients?.[0];

      const ccContacts = recipients?.slice(1);

      await sendBookingConfirmNotification({
        requestId,
        senderContactId,
        toContact,
        ccContacts,
        overrides,
      });

      toast({
        title: 'Success',
        description: overrides?.clipboard ? 'Email copied to clipboard' : 'Email sent successfully',
        status: 'success',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error?.message,
        status: 'error',
      });
    }
  };

  const showWarning = useMemo(() => {
    if (!recipients?.length) return false;

    return hasCustomerEmail(recipients);
  }, [recipients]);

  const canSend = !!recipients?.length;

  const shouldConfirmSpotRate = useMemo(
    () => getTenantByHost().isSystemTenant && modelUtils.request.isUnconfirmedSpotBooking(request, quotes),
    [request, quotes],
  );

  return (
    <SimpleForm {...methods} submitOnBlur={false}>
      <EmailDrawer isOpen={isOpen} onClose={onClose}>
        <EmailDrawerLeftPanel title="Specify Contact(s)">
          <SimpleCustomerRecipientForm
            name="recipients"
            defaultAccount={request?.account}
            defaultSelectedContacts={defaultSelectedContacts}
            searchPattern="FF"
          />
        </EmailDrawerLeftPanel>
        <EmailDrawerRightPanel title="Send Booking">
          <EmailQuoteForm
            sendLongerEmail
            request={defaultRequest}
            onSubmit={handleSendEmail}
            onClose={onClose}
            isLoading={isLoading}
            canSend={canSend}
            hasCustomerEmail={showWarning}
            requireSpotConfirmation={shouldConfirmSpotRate}
          />
        </EmailDrawerRightPanel>
      </EmailDrawer>
    </SimpleForm>
  );
};
